/**
 * Order Detail Modal Component - Using StandardModal
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../components/ui/modal';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { ClipboardList, Printer } from 'lucide-react';

const statusLabels = {
  aberta: 'Aberta',
  fechada: 'Fechada',
  cancelada: 'Cancelada',
};

const statusVariants = {
  aberta: 'default',
  fechada: 'secondary',
  cancelada: 'destructive',
};

export function OrderDetailModal({ 
  open, 
  onOpenChange, 
  order, 
  getTableNumber, 
  getCustomerName, 
  formatCurrency, 
  formatDate 
}) {
  const { t } = useTranslation();

  const handlePrint = () => {
    window.print();
  };

  if (!order) return null;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={
        <span className="flex items-center gap-2">
          <ClipboardList className="h-5 w-5" />
          Comanda #{order.id?.slice(-6)}
        </span>
      }
      size="default"
      footer={
        <Button className="w-full" variant="outline" onClick={handlePrint}>
          <Printer className="h-4 w-4 mr-2" />
          Imprimir Comanda
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">{t('orders.table')}</p>
            <p className="font-medium">
              {order.table_id ? `Mesa ${getTableNumber(order.table_id)}` : 'Balcão'}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">{t('orders.customer')}</p>
            <p className="font-medium">
              {order.customer_id ? getCustomerName(order.customer_id) : 'Não identificado'}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">Data</p>
            <p className="font-medium">{formatDate(order.created_at, true)}</p>
          </div>
          <div>
            <p className="text-muted-foreground">{t('orders.status')}</p>
            <Badge variant={statusVariants[order.status]}>
              {statusLabels[order.status]}
            </Badge>
          </div>
        </div>

        <div className="border-t pt-4">
          <p className="font-medium mb-2">{t('orders.items')}</p>
          <ScrollArea className="h-48">
            <div className="space-y-2">
              {order.items?.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm p-2 bg-muted rounded">
                  <div>
                    <p className="font-medium">{item.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {item.quantity}x {formatCurrency(item.unit_price)}
                      {item.discount > 0 && ` (-${formatCurrency(item.discount)})`}
                    </p>
                  </div>
                  <span className="font-mono">
                    {formatCurrency(item.quantity * item.unit_price - (item.discount || 0))}
                  </span>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        <div className="border-t pt-4 space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('orders.subtotal')}</span>
            <span className="font-mono">{formatCurrency(order.subtotal)}</span>
          </div>
          {order.discount > 0 && (
            <div className="flex justify-between text-sm text-destructive">
              <span>{t('orders.discount')}</span>
              <span className="font-mono">-{formatCurrency(order.discount)}</span>
            </div>
          )}
          <div className="flex justify-between text-lg font-bold">
            <span>{t('orders.total')}</span>
            <span className="font-mono text-primary">{formatCurrency(order.total)}</span>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default OrderDetailModal;
